import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useMobileNav } from '../hooks/useMobileNav';
import { ClockInOutFAB } from '../components/ClockInOutFAB';
import { ClockStatusCard } from '../components/ClockStatusCard';
import { DailyRoster } from '../components/DailyRoster';
import { StaffIDCardModal } from '../components/StaffIDCardModal';

function DriverDashboard({ user, organization, onLogout }) {
  const mobileNav = useMobileNav();
  const [activeTab, setActiveTab] = useState('manifests');
  const [manifests, setManifests] = useState([]);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  // Side-nav launches the same digital ID badge modal that staff /
  // managers see — drivers need a verifiable identification when picking
  // children up at the school gate, so this is just as important for
  // them (arguably more so).
  const [showStaffIdModal, setShowStaffIdModal] = useState(false);

  useEffect(() => {
    loadManifests();
  }, [selectedDate]);

  const loadManifests = async () => {
    setLoading(true);
    try {
      // Fetch route manifests for the selected date
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manifests?date=${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setManifests(data);
      } else {
        setManifests([]);
      }
    } catch (err) {
      console.error('Error loading manifests:', err);
      setManifests([]);
    }
    setLoading(false);
  };

  const handleConfirmPickup = async (manifestId, childId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manifests/${manifestId}/confirm-pickup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          child_id: childId,
          timestamp: new Date().toISOString(),
          location: null // TODO: Add geolocation
        })
      });

      if (response.ok) {
        alert('Pickup confirmed!');
        loadManifests();
      }
    } catch (err) {
      alert('Error confirming pickup: ' + err.message);
    }
  };

  const handleConfirmDropoff = async (manifestId, childId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manifests/${manifestId}/confirm-dropoff`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          child_id: childId,
          timestamp: new Date().toISOString(),
          location: null // TODO: Add geolocation
        })
      });

      if (response.ok) {
        alert('Drop-off confirmed!');
        loadManifests();
      }
    } catch (err) {
      alert('Error confirming drop-off: ' + err.message);
    }
  };

  const handleStartRoute = async (manifestId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manifests/${manifestId}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Route started!');
        loadManifests();
      }
    } catch (err) {
      alert('Error starting route: ' + err.message);
    }
  };

  const handleCompleteRoute = async (manifestId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manifests/${manifestId}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Route completed!');
        setSelectedManifest(null);
        loadManifests();
      }
    } catch (err) {
      alert('Error completing route: ' + err.message);
    }
  };

  const getRouteTypeLabel = (routeType) => {
    const labels = {
      'home_to_school': 'Home → School',
      'club_to_school': 'Club → School',
      'school_to_club': 'School → Club',
      'school_to_home': 'School → Home',
      'club_to_home': 'Club → Home'
    };
    return labels[routeType] || routeType;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'pending': 'badge-yellow',
      'in_progress': 'badge-blue',
      'completed': 'badge-green'
    };
    return classes[status] || 'badge-gray';
  };

  return (
    <div className="dashboard">
      <button
        type="button"
        className="mobile-nav-toggle"
        onClick={mobileNav.toggle}
        aria-label="Toggle navigation"
        data-testid="mobile-nav-toggle"
      >
        {mobileNav.isOpen ? '✕' : '☰'}
      </button>
      <div className={`mobile-nav-backdrop ${mobileNav.isOpen ? 'show' : ''}`} onClick={mobileNav.close}></div>
      <div className={`sidebar ${mobileNav.isOpen ? 'mobile-open' : ''}`} onClick={(e) => {
        if (e.target.closest('.nav-item')) mobileNav.close();
      }}>
        <div className="sidebar-header" data-testid="sidebar-header">
          {organization && organization.settings && organization.settings.logo_url ? (
            <>
              <img
                src={organization.settings.logo_url}
                alt={organization.name}
                style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain', marginBottom: '0.5rem' }}
                data-testid="sidebar-org-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <h2 style={{ fontSize: '1rem', margin: 0 }}>🚐 {organization.name}</h2>
            </>
          ) : (
            <h2>🚐 {organization ? `${organization.name} Transport` : 'Kids WiseApp Transport'}</h2>
          )}
          <p>{user.full_name}</p>
          <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Driver</p>
        </div>

        <ul className="nav-menu">
          <li 
            className={`nav-item ${activeTab === 'manifests' ? 'active' : ''}`}
            onClick={() => setActiveTab('manifests')}
            data-testid="nav-manifests"
          >
            📋 Daily Manifests
          </li>
          <li
            className={`nav-item ${activeTab === 'clock' ? 'active' : ''}`}
            onClick={() => setActiveTab('clock')}
            data-testid="nav-clock"
          >
            🕒 Clock In/Out
          </li>
          <li
            className={`nav-item ${activeTab === 'roster' ? 'active' : ''}`}
            onClick={() => setActiveTab('roster')}
            data-testid="nav-roster"
          >
            🧒 Children's Register
          </li>
          <li
            className="nav-item"
            onClick={() => setShowStaffIdModal(true)}
            data-testid="nav-staff-id-card"
            title="Show or share my staff ID card"
          >
            🪪 My ID Card
          </li>
          <li 
            className="nav-item logout-btn" 
            onClick={onLogout}
            data-testid="logout-btn"
          >
            🚪 Logout
          </li>
        </ul>
      </div>

      <div className="main-content">
        {activeTab === 'manifests' && (
          <>
            <div className="page-header">
              <h1>🚐 Daily Route Manifests</h1>
              <p>View and manage your daily transport routes</p>
            </div>

            {/* Date Selector */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="form-group" style={{ margin: 0, maxWidth: '300px' }}>
                <label>Select Date</label>
                <input 
                  type="date" 
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  data-testid="date-selector"
                />
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading manifests...</div>
            ) : selectedManifest ? (
              /* Detailed Manifest View */
              <div>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setSelectedManifest(null)}
                  style={{ marginBottom: '1rem' }}
                  data-testid="back-to-list-btn"
                >
                  ← Back to List
                </button>

                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div>
                      <h2 style={{ margin: '0 0 0.5rem 0' }}>
                        {getRouteTypeLabel(selectedManifest.route_type)}
                      </h2>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className={`badge ${getStatusBadgeClass(selectedManifest.status)}`}>
                          {selectedManifest.status.replace('_', ' ').toUpperCase()}
                        </span>
                        <span style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                          {new Date(selectedManifest.route_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    <div style={{ textAlign: 'right' }}>
                      {selectedManifest.status === 'pending' && (
                        <button 
                          className="btn btn-primary" 
                          onClick={() => handleStartRoute(selectedManifest._id || selectedManifest.id)}
                          data-testid="start-route-btn"
                        >
                          ▶ Start Route
                        </button>
                      )}
                      {selectedManifest.status === 'in_progress' && (
                        <button 
                          className="btn btn-primary" 
                          onClick={() => handleCompleteRoute(selectedManifest._id || selectedManifest.id)}
                          data-testid="complete-route-btn"
                        >
                          ✓ Complete Route
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Route Summary */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '1rem',
                    padding: '1rem',
                    background: 'hsl(var(--muted))',
                    borderRadius: '8px'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>
                        DEPARTURE
                      </div>
                      <div style={{ fontWeight: '600' }}>{selectedManifest.departure_time}</div>
                      <div style={{ fontSize: '0.875rem' }}>{selectedManifest.start_point}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>
                        TARGET ARRIVAL
                      </div>
                      <div style={{ fontWeight: '600' }}>{selectedManifest.target_completion_time}</div>
                      <div style={{ fontSize: '0.875rem' }}>{selectedManifest.end_point}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>
                        CHILDREN
                      </div>
                      <div style={{ fontWeight: '600', fontSize: '1.5rem' }}>
                        {selectedManifest.children_collected}/{selectedManifest.total_children}
                      </div>
                    </div>
                  </div>
                </div>

                {/* School Stops */}
                {selectedManifest.school_stops && selectedManifest.school_stops.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <h3>School Stops</h3>
                    {selectedManifest.school_stops.map((stop, idx) => (
                      <div key={idx} className="card" style={{ borderLeft: '4px solid hsl(var(--primary))' }}>
                        <div style={{ marginBottom: '1rem' }}>
                          <h3 style={{ margin: '0 0 0.5rem 0' }}>{stop.school_name}</h3>
                          <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                            {stop.school_address}
                            {stop.drop_off_point && ` • ${stop.drop_off_point}`}
                          </div>
                        </div>

                        {/* Children at this stop */}
                        {stop.children && stop.children.length > 0 && (
                          <div>
                            <h4 style={{ fontSize: '0.875rem', marginBottom: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                              Children ({stop.children.length})
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                              {stop.children.map((child) => (
                                <div 
                                  key={child.child_id}
                                  style={{ 
                                    padding: '1rem', 
                                    background: child.collected ? 'hsl(145 63% 92%)' : 'hsl(var(--background))',
                                    border: '1px solid hsl(var(--border))',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}
                                  data-testid={`child-${child.child_id}`}
                                >
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                      {child.child_name}
                                    </div>
                                    <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                                      {child.school_class && `Class: ${child.school_class}`}
                                      {child.teacher_name && ` • Teacher: ${child.teacher_name}`}
                                    </div>
                                    {child.meeting_point && (
                                      <div style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                        📍 {child.meeting_point}
                                      </div>
                                    )}
                                    {child.special_instructions && (
                                      <div style={{ 
                                        fontSize: '0.875rem', 
                                        marginTop: '0.5rem',
                                        padding: '0.5rem',
                                        background: 'hsl(45 96% 90%)',
                                        borderRadius: '4px'
                                      }}>
                                        ⚠️ {child.special_instructions}
                                      </div>
                                    )}
                                  </div>

                                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    {!child.collected && selectedManifest.status === 'in_progress' && (
                                      <button 
                                        className="btn btn-primary" 
                                        onClick={() => handleConfirmPickup(selectedManifest._id || selectedManifest.id, child.child_id)}
                                        data-testid={`confirm-pickup-${child.child_id}`}
                                      >
                                        {selectedManifest.route_type.includes('school') && selectedManifest.route_type.includes('to') ? 'Confirm Drop-off' : 'Confirm Pickup'}
                                      </button>
                                    )}
                                    {child.collected && (
                                      <span className="badge badge-green">
                                        ✓ {child.collected_at ? new Date(child.collected_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'Confirmed'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : manifests.length > 0 ? (
              /* Manifest List View */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {manifests.map((manifest) => (
                  <div 
                    key={manifest._id || manifest.id} 
                    className="card" 
                    style={{ 
                      cursor: 'pointer',
                      borderLeft: `4px solid ${
                        manifest.status === 'completed' ? 'hsl(145 63% 42%)' :
                        manifest.status === 'in_progress' ? 'hsl(220 90% 60%)' :
                        'hsl(45 92% 50%)'
                      }`
                    }}
                    onClick={() => setSelectedManifest(manifest)}
                    data-testid={`manifest-${manifest._id || manifest.id}`}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: '0 0 0.5rem 0' }}>
                          {getRouteTypeLabel(manifest.route_type)}
                        </h3>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                          <span className={`badge ${getStatusBadgeClass(manifest.status)}`}>
                            {manifest.status.replace('_', ' ').toUpperCase()}
                          </span>
                          <span style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                            {manifest.departure_time} → {manifest.target_completion_time}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                          {manifest.start_point} → {manifest.end_point}
                        </div>
                        <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          <strong>{manifest.total_children}</strong> children • 
                          <strong> {manifest.school_stops ? manifest.school_stops.length : 0}</strong> school stops
                        </div>
                      </div>
                      <div style={{ fontSize: '2rem' }}>
                        {manifest.status === 'completed' ? '✓' : 
                         manifest.status === 'in_progress' ? '▶' : '⏸'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚐</div>
                <h3>No routes scheduled</h3>
                <p style={{ color: 'hsl(var(--muted-foreground))' }}>
                  No transport routes have been generated for {new Date(selectedDate).toLocaleDateString()}.
                </p>
              </div>
            )}
          </>
        )}

        {/* Clock In/Out tab — same UX as staff, uses the bug-fixed
            /api/clock-events/my-status endpoint so an overtime shift
            crossing midnight still shows the correct state. */}
        {activeTab === 'clock' && (
          <>
            <div className="page-header">
              <h1>🕒 Clock In/Out</h1>
              <p>Track your working hours</p>
            </div>
            <ClockStatusCard
              onOpenRegister={() => setActiveTab('roster')}
            />
          </>
        )}

        {/* Children's Register — opened up to drivers (backend RBAC was
            widened to include 'driver' on the attendance endpoints). */}
        {activeTab === 'roster' && (
          <>
            <div className="page-header">
              <h1>🧒 Children's Register</h1>
              <p>Mark children as present, absent, or departed</p>
            </div>
            <DailyRoster allowEdit={true} />
          </>
        )}
      </div>

      {/* Floating clock + register quick-access — visible on every
          driver page except the clock/roster tabs (which already have
          the equivalent UI). */}
      <ClockInOutFAB
        activeTab={activeTab}
        onNavigate={(tab) => setActiveTab(tab)}
      />

      {/* Staff ID badge modal — opens from the side-nav "🪪 My ID Card".
          Drivers see the same digital badge + QR-verification flow as
          everyone else who collects children on the org's behalf. */}
      <StaffIDCardModal
        open={showStaffIdModal}
        onClose={() => setShowStaffIdModal(false)}
      />
    </div>
  );
}

export default DriverDashboard;
